/**
 * PendingInvitationsReminder — after sign-in, lists the consortium invitations the user has not
 * answered yet (including ones sent to their email before they had an account) and lets them
 * accept or decline each. "Later" hides it for this browser session; it comes back next time.
 */
import React, { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Avatar,
  Alert, CircularProgress, List, ListItem, ListItemAvatar, ListItemText, Link,
} from '@mui/material';
import { Groups as GroupsIcon } from '@mui/icons-material';
import { axiosService, fetcher } from 'shared/helpers/axios';
import { useSnackbar } from 'shared/contexts/SnackbarContext';

const DISMISS_KEY = 'otable.pendingInvitationsDismissed';

const isDismissed = () => {
  try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
};

export default function PendingInvitationsReminder({ user }) {
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  const { showSnackbar } = useSnackbar() || {};
  const [dismissed, setDismissed] = useState(isDismissed);
  const [busy, setBusy] = useState(null);           // id of the invitation being answered
  const [error, setError] = useState(null);         // { id, message, needsOrg }

  const { data, mutate } = useSWR(
    user?.id && !dismissed ? '/interact/consortium-invitations/pending-for-me/' : null,
    fetcher,
    { revalidateOnFocus: false }
  );
  const invitations = Array.isArray(data) ? data : (data?.results ?? []);

  if (dismissed || invitations.length === 0) return null;

  const later = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode: just close */ }
    setDismissed(true);
  };

  const answer = async (ci, verb) => {
    setBusy(ci.id); setError(null);
    try {
      await axiosService.post(`/interact/consortium-invitations/${ci.id}/${verb}/`);
      const name = ci.consortium?.name || 'the consortium';
      showSnackbar?.(verb === 'accept' ? `You joined ${name}.` : `Invitation to ${name} declined.`,
                     verb === 'accept' ? 'success' : 'info');
      await mutate(invitations.filter((i) => i.id !== ci.id), { revalidate: true });
      if (verb === 'accept') {
        globalMutate((key) => typeof key === 'string' && key.startsWith('/consortium/consortiums/'));
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Something went wrong. Please try again.';
      setError({ id: ci.id, message: msg, needsOrg: /primary organization/i.test(msg) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={later}>
      <DialogTitle>
        {invitations.length === 1 ? 'You have a consortium invitation' : `You have ${invitations.length} consortium invitations`}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 2, pb: 1 }}>
          These were sent to {user?.email || 'your email'}. Accept to join, or decline to let the coordinator know.
        </Typography>
        <List disablePadding>
          {invitations.map((ci) => {
            const from = ci.invitation?.from_user;
            const consortiumId = ci.consortium?.id;
            const rowError = error?.id === ci.id ? error : null;
            return (
              <ListItem key={ci.id} divider alignItems="flex-start" sx={{ flexWrap: 'wrap', px: 3 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}><GroupsIcon /></Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    consortiumId
                      ? <Link component="button" variant="subtitle1" fontWeight={600} underline="hover"
                          onClick={() => { later(); navigate(`/consortium/${consortiumId}/`); }}>
                          {ci.consortium.name}
                        </Link>
                      : <Typography variant="subtitle1" fontWeight={600}>Consortium</Typography>
                  }
                  secondary={from?.name ? `Invited by ${from.name}` : null}
                />
                <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignSelf: 'center' }}>
                  <Button size="small" color="inherit" disabled={!!busy} onClick={() => answer(ci, 'reject')}>
                    Decline
                  </Button>
                  <Button size="small" variant="contained" disabled={!!busy} onClick={() => answer(ci, 'accept')}
                    startIcon={busy === ci.id ? <CircularProgress size={14} color="inherit" /> : null}>
                    Accept
                  </Button>
                </Box>
                {rowError && (
                  <Alert severity="warning" sx={{ mt: 1.5, width: '100%' }}
                    action={rowError.needsOrg && user?.id
                      ? <Button color="inherit" size="small"
                          onClick={() => { later(); navigate(`/profile/${user.id}/`); }}>
                          Open profile
                        </Button>
                      : null}>
                    {rowError.needsOrg
                      ? 'Add your organization to your profile and set it as primary, then accept the invitation.'
                      : rowError.message}
                  </Alert>
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={later}>Later</Button>
      </DialogActions>
    </Dialog>
  );
}
